import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

/** "2026-10-16" for a date input, `offset` days from today in the user's own
 * calendar (toISOString alone would give yesterday before 05:30 in India). */
function isoDay(offset) {
  const when = new Date();
  when.setDate(when.getDate() + offset);
  const pad = (number) => String(number).padStart(2, '0');
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
}

/** The server's date rules (OfferTerms), checked before sending. */
function dateErrors(form) {
  const today = isoDay(0);
  const errors = {};
  if (form.starts_on && form.starts_on < today) {
    errors.starts_on = 'This date has passed. Pick today or a later date.';
  }
  if (form.ends_on && form.starts_on && form.ends_on < form.starts_on) {
    errors.ends_on = 'The end date cannot be before the start date.';
  }
  if (form.respond_by && form.respond_by < today) {
    errors.respond_by = 'This date has passed. Pick today or a later date.';
  } else if (form.respond_by && form.starts_on && form.respond_by > form.starts_on) {
    errors.respond_by = 'Ask for an answer on or before the start date.';
  }
  return errors;
}

const EMPTY = {
  title: '',
  starts_on: isoDay(45),
  ends_on: isoDay(225),
  weekly_hours: 32,
  location: '',
  work_mode: 'ONSITE',
  stipend_amount: '',
  stipend_currency: 'EUR',
  stipend_period: 'MONTHLY',
  message: '',
  terms: '',
  respond_by: isoDay(14),
};

/**
 * Writes a draft offer, or edits one. Sending is a separate, deliberate step,
 * so this dialog only ever saves — with one exception the caller opts into.
 */
export function OfferDialog({ open, onClose, applicationId, offer = null, defaultTitle }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(offer);
  const [form, setForm] = useState(() =>
    offer
      ? {
          title: offer.title,
          starts_on: offer.starts_on,
          ends_on: offer.ends_on ?? '',
          weekly_hours: offer.weekly_hours ?? '',
          location: offer.location ?? '',
          work_mode: offer.work_mode,
          stipend_amount: offer.stipend_amount ?? '',
          stipend_currency: offer.stipend_currency,
          stipend_period: offer.stipend_period,
          message: offer.message ?? '',
          terms: offer.terms ?? '',
          respond_by: offer.respond_by,
        }
      : { ...EMPTY, title: defaultTitle ?? '' },
  );

  const save = useMutation({
    mutationFn: ({ values, sendNow }) =>
      isEdit
        ? companyApi.updateOffer(offer.id, values)
        : companyApi.createOffer({
            ...values,
            application_id: applicationId,
            send_now: sendNow,
          }),
    onSuccess: (_, variables) => {
      toast.success(isEdit ? 'Draft updated' : variables.sendNow ? 'Offer sent' : 'Draft saved');
      void queryClient.invalidateQueries({ queryKey: ['company-offers'] });
      void queryClient.invalidateQueries({ queryKey: ['offer-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['company-application'] });
      void queryClient.invalidateQueries({ queryKey: ['company-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['company-application-counts'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save the offer')),
  });

  // Errors show once a save has been tried, then follow every change.
  const [checked, setChecked] = useState(false);
  const errors = checked ? dateErrors(form) : {};
  const today = isoDay(0);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submitWith(sendNow) {
    setChecked(true);
    if (Object.keys(dateErrors(form)).length > 0) return;
    save.mutate({ values: values(), sendNow });
  }

  function values() {
    return {
      title: form.title,
      starts_on: form.starts_on,
      ends_on: form.ends_on || null,
      weekly_hours: form.weekly_hours ? Number(form.weekly_hours) : null,
      location: form.location || null,
      work_mode: form.work_mode,
      stipend_amount: form.stipend_amount === '' ? null : Number(form.stipend_amount),
      stipend_currency: form.stipend_currency,
      stipend_period: form.stipend_period,
      message: form.message || null,
      terms: form.terms || null,
      respond_by: form.respond_by,
    };
  }

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit the draft' : 'Make an offer'}
      description="The candidate sees every field here once you send it."
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submitWith(false);
        }}
      >
        <Input
          label="Role"
          required
          placeholder="Backend Engineering Intern"
          value={form.title}
          onChange={(event) => update('title', event.target.value)}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Starts"
            type="date"
            required
            min={today}
            error={errors.starts_on}
            value={form.starts_on}
            onChange={(event) => update('starts_on', event.target.value)}
          />
          <Input
            label="Ends"
            type="date"
            hint="Optional."
            min={form.starts_on && form.starts_on > today ? form.starts_on : today}
            error={errors.ends_on}
            value={form.ends_on}
            onChange={(event) => update('ends_on', event.target.value)}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Stipend"
            type="number"
            min={0}
            step={50}
            placeholder="1200"
            value={form.stipend_amount}
            onChange={(event) => update('stipend_amount', event.target.value)}
          />
          <Input
            label="Currency"
            maxLength={3}
            value={form.stipend_currency}
            onChange={(event) => update('stipend_currency', event.target.value.toUpperCase())}
          />
          <Select
            label="Per"
            value={form.stipend_period}
            onChange={(event) => update('stipend_period', event.target.value)}
          >
            <option value="HOURLY">Hour</option>
            <option value="WEEKLY">Week</option>
            <option value="MONTHLY">Month</option>
            <option value="TOTAL">Whole placement</option>
          </Select>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Hours a week"
            type="number"
            min={1}
            max={60}
            value={form.weekly_hours}
            onChange={(event) => update('weekly_hours', event.target.value)}
          />
          <Select
            label="Work mode"
            value={form.work_mode}
            onChange={(event) => update('work_mode', event.target.value)}
          >
            <option value="ONSITE">On site</option>
            <option value="HYBRID">Hybrid</option>
            <option value="REMOTE">Remote</option>
          </Select>
          <Input
            label="Answer by"
            type="date"
            required
            hint="Before the start date."
            min={today}
            max={form.starts_on || undefined}
            error={errors.respond_by}
            value={form.respond_by}
            onChange={(event) => update('respond_by', event.target.value)}
          />
        </div>

        <Input
          label="Location"
          placeholder="Amsterdam"
          value={form.location}
          onChange={(event) => update('location', event.target.value)}
        />

        <Textarea
          label="Message"
          rows={3}
          placeholder="We would love to have you on the platform team."
          value={form.message}
          onChange={(event) => update('message', event.target.value)}
        />
        <Textarea
          label="Terms"
          rows={3}
          placeholder="Six months, 32 hours a week, standard internship agreement."
          value={form.terms}
          onChange={(event) => update('terms', event.target.value)}
        />

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="secondary" isLoading={save.isPending}>
            Save draft
          </Button>
          {!isEdit && (
            <Button
              type="button"
              isLoading={save.isPending}
              onClick={() => submitWith(true)}
            >
              Save and send
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}
